/** @jsxImportSource @emotion/react */
import { Field, useFormikContext } from 'formik'

import states from 'constants/states'
import { TextInput } from 'components/atoms/Input'
import { SelectInput } from 'components/atoms/Select'
import { CheckboxInput } from 'components/atoms/Checkbox'
import { MaskedTextInput } from 'components/atoms/InputMask'

export enum ClaimSubmissionMethod {
  ELECTRONIC = 'ELECTRONIC',
  PAPER = 'PAPER',
}

/**
 * The payer's plan class. Mirrors PayerType on the back end — declared here
 * rather than imported, like every other enum this form uses.
 *
 * Client Bill carries behaviour: an order whose primary payer is client-billed
 * is marked billed on arrival and never appears in Billing Review, because the
 * client is invoiced rather than the plan.
 */
export enum PayerType {
  IN_NETWORK = 'IN_NETWORK',
  OUT_OF_NETWORK = 'OUT_OF_NETWORK',
  MEDICARE = 'MEDICARE',
  CLIENT_BILL = 'CLIENT_BILL',
}

export const PAYER_TYPE_LABELS: Record<PayerType, string> = {
  [PayerType.IN_NETWORK]: 'In Network',
  [PayerType.OUT_OF_NETWORK]: 'Out of Network',
  [PayerType.MEDICARE]: 'Medicare',
  [PayerType.CLIENT_BILL]: 'Client Bill',
}

const PAYER_TYPE_OPTIONS = Object.values(PayerType).map((value) => ({
  key: value,
  value: PAYER_TYPE_LABELS[value],
}))

const CLAIM_SUBMISSION_METHOD_OPTIONS = [
  { key: ClaimSubmissionMethod.ELECTRONIC, value: 'Electronic' },
  { key: ClaimSubmissionMethod.PAPER, value: 'Paper' },
]

export interface LabPayerFormValues {
  name: string
  verificationLink: string
  telephone: string
  ourProviderId: string
  addressStreetLine1: string
  addressStreetLine2: string
  addressCity: string
  addressState: string
  addressZip: string
  uPayerId: string
  planId: string
  active: boolean
  payerType: PayerType
  outsourceBlocked: boolean
  notes: string
  // billingLocationCode: string
  claimFileIndicator?: string
  placeOfService?: string
  claimSubmissionMethod?: ClaimSubmissionMethod | ''
  providerTaxonomyCode?: string
  providerCommercialNumber?: string
  timelyFilingDays?: number | string
  optumPayerId?: string
  optumClaimsPayerId?: string
  aiVerificationEnabled?: boolean
  isTestSmarterBilling?: boolean
}

export const LabPayerForm = () => {
  const { values, initialValues, touched } = useFormikContext<LabPayerFormValues>()
  const isNameModified = touched.name && values.name !== initialValues.name

  return (
    <>
      <div className="row">
        <div className="col-12 form-group">
          <label className="textGray">Name</label>
          <Field component={TextInput} label={false} placeholder="Payor Name" name="name" />
          <div
            className={isNameModified ? 'text-danger' : 'text-muted'}
            css={{ fontSize: '0.875rem' }}>
            Changes could affect insurance auto-verification and should not be made without
            consideration.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 form-group">
          <label className="textGray">Verification Link</label>
          <Field
            component={TextInput}
            label={false}
            placeholder="Verification Link"
            name="verificationLink"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12 form-group">
          <label className="textGray">Telephone Number</label>
          <Field component={MaskedTextInput} name="telephone" mask="(999) 999-9999" />
        </div>
      </div>
      <div className="row">
        <div className="col-4 form-group">
          <label className="textGray">Payor ID</label>
          <Field component={TextInput} name="uPayerId" />
        </div>
        <div className="col-4 form-group">
          <label className="textGray">TS Provider ID</label>
          <Field component={TextInput} name="ourProviderId" />
        </div>
        <div className="col-4 form-group">
          <label className="textGray">CureMD Plan ID</label>
          <Field component={TextInput} name="planId" />
        </div>
        {/*<div className="col-4 form-group">*/}
        {/*  <Field*/}
        {/*    component={SelectInput}*/}
        {/*    options={BILLING_LOCATION_CODES}*/}
        {/*    labelClassName="textGray"*/}
        {/*    name="billingLocationCode"*/}
        {/*    label="Billing Location Code"*/}
        {/*  />*/}
        {/*</div>*/}
        <div className="col-4 form-group">
          <label className="textGray" title="Trading partner ID used for Optum eligibility checks (e.g. NEBCBS)">
            Optum Eligibility Payer ID
          </label>
          <Field component={TextInput} name="optumPayerId" />
        </div>
        <div className="col-4 form-group">
          <label
            className="textGray"
            title="CPID used for Optum professional claim submission (e.g. 1417). Differs from the eligibility ID for most payers.">
            Optum Claims Payer ID (CPID)
          </label>
          <Field component={TextInput} name="optumClaimsPayerId" />
        </div>
      </div>
      <div className="row">
        <div className="col-12 form-group">
          <label className="textGray">Address</label>
          <Field
            component={TextInput}
            label={false}
            placeholder="Street Address"
            name="addressStreetLine1"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12 form-group">
          <Field
            component={TextInput}
            label={false}
            placeholder="Suite/Apt"
            name="addressStreetLine2"
          />
        </div>
        <div className="col-12"></div>
      </div>
      <div className="row">
        <div className="col-4">
          <label className="textGray">City</label>
          <Field component={TextInput} label={false} placeholder="City" name="addressCity" />
        </div>
        <div className="col-4">
          <Field
            component={SelectInput}
            options={states || []}
            defaultLabel="State"
            labelClassName="textGray"
            name="addressState"
            label="State"
          />
        </div>
        <div className="col-4">
          <label className="textGray">Zip Code</label>
          <Field component={TextInput} label={false} placeholder="Zip Code" name="addressZip" />
        </div>
      </div>
      <div className="row">
        <div className="col-4 form-group">
          <Field
            component={SelectInput}
            noDefault
            options={PAYER_TYPE_OPTIONS}
            labelClassName="textGray"
            name="payerType"
            label="Payor Type"
          />
          {values.payerType === PayerType.CLIENT_BILL && (
            <div className="text-muted" css={{ fontSize: '0.75rem', marginTop: '0.2rem' }}>
              Orders with this payor as primary are marked billed automatically and skip Billing
              Review.
            </div>
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-12 form-group">
          <label className="textGray">Notes</label>
          <Field component="textarea" name="notes" placeholder="Notes" className="form-control" />
        </div>
      </div>
      <div className="row ml-2" css={{ gap: 10 }}>
        <Field
          id="aiVerificationEnabled"
          name="aiVerificationEnabled"
          component={CheckboxInput}
          label="Auto AI Verification"
        />
        <Field
          id="isTestSmarterBilling"
          name="isTestSmarterBilling"
          component={CheckboxInput}
          label="TestSmarter Billing"
        />
        <Field
          id="outsourceBlocked"
          name="outsourceBlocked"
          component={CheckboxInput}
          label="Block outsource access (requires payer approval)"
        />
        <Field id="active" name="active" component={CheckboxInput} label="Active" />
      </div>
    </>
  )
}

export const LabPayerBillingForm = () => (
  <>
    <div className="row">
      <div className="col-6 form-group">
        <Field
          component={SelectInput}
          noDefault
          options={[
            { key: false, value: 'Default' },
            { key: 'BL', value: 'BL: Blue Cross / Blue Shield' },
            { key: 'CI', value: 'Commercial Insurance' },
            { key: 'DS', value: 'DS: Disability' },
            { key: 'HM', value: 'HM: Health Maintenance Organization (HMO)' },
            { key: 'MA', value: 'MA: Medicare Part A' },
            { key: 'MB', value: 'MB: Medicare Part B' },
            { key: 'MC', value: 'MC: Medicaid' },
            { key: 'CH', value: 'CH: Tricare' },
          ]}
          labelClassName="textGray"
          name="claimFileIndicator"
          label="Claim Filing Indicator (CFI)"
        />
      </div>
      <div className="col-6 form-group">
        <Field
          component={SelectInput}
          noDefault
          options={[
            { key: false, value: 'Default' },
            { key: '11', value: '11: Office' },
            { key: '12', value: '12: Home' },
            { key: '81', value: '81: Independent Laboratory' },
          ]}
          labelClassName="textGray"
          name="placeOfService"
          label="Place Of Service (POS)"
        />
      </div>
      <div className="col-6 form-group">
        <Field
          component={SelectInput}
          noDefault
          options={CLAIM_SUBMISSION_METHOD_OPTIONS}
          labelClassName="textGray"
          name="claimSubmissionMethod"
          label="Claim Submission Method"
        />
      </div>
    </div>
    <div className="row">
      <div className="col-6 form-group">
        <label className="textGray">Provider Taxonomy Code</label>
        <Field
          component={TextInput}
          label={false}
          placeholder="Provider Taxonomy Code"
          name="providerTaxonomyCode"
        />
      </div>
      <div className="col-6 form-group">
        <label className="textGray">Provider Commercial Number</label>
        <Field
          component={TextInput}
          label={false}
          placeholder="Provider Commercial Number"
          name="providerCommercialNumber"
        />
      </div>
      <div className="col-6 form-group">
        <label className="textGray">Timely Filing (days)</label>
        <Field
          component={TextInput}
          type="number"
          minValue="0"
          label={false}
          placeholder="Number of days"
          name="timelyFilingDays"
        />
      </div>
    </div>
  </>
)
